import { FT5_OFFERS_ACTION_TRACKING, FT5_OFFERS_ARTICLE_OPTION_CHANGE } from '../../_common/client/offers-tracking';
import { FeatureTracking, FeatureTrackingOffersVariationService } from '../offers-tracking-model';
import { FeatureTrackingHelper } from './feature-tracking-helper';
import { ArticleOptionsSelectionChange } from '../../global';
import cloneDeep from 'lodash/cloneDeep';
import { eventQBus } from '@otto-ec/global-resources/event-q-bus';
import { Action, ActionName, Feature, submitEvent } from '@otto-ec/tracking-bct';

export type ActionTrackData = {
    actionName: string;
    variationId: string;
};

export class ActionTracking {
    constructor(private featureTrackingHelper: FeatureTrackingHelper) {}

    public registerTracking() {
        this.registerClickActionTracking();
        this.registerArticleOptionChangeListener();
    }

    private trackAction(actionName: string, featureTracking: FeatureTracking) {
        const features: Feature[] = this.featureTrackingHelper.generateFeatureTrackingOutput(featureTracking) as never as Feature[];
        const action: Action = {
            name: actionName as never as ActionName,
            features,
        };
        submitEvent({}, action);
    }

    private registerClickActionTracking() {
        eventQBus.on(FT5_OFFERS_ACTION_TRACKING, (trackingData: ActionTrackData) => {
            const { actionName, variationId } = trackingData;
            const featureTracking = this.createFeatureTrackingsWithSingleOffer(variationId);
            featureTracking.offerVariations[0].status = 'visible';
            this.trackAction(actionName, featureTracking);
        });
    }

    private registerArticleOptionChangeListener() {
        const updateSelectedServices = (data: ArticleOptionsSelectionChange) => {
            const featureTrackingsWithSingleOffer = this.createFeatureTrackingsWithSingleOffer(data.variationId);
            featureTrackingsWithSingleOffer.offerVariations[0].status = 'visible';
            const changedService = this.changeService(data.variationId, data.changedServiceId, data.isSelected);
            this.trackSelectionChangeAction(featureTrackingsWithSingleOffer, changedService);
        };
        eventQBus.on(FT5_OFFERS_ARTICLE_OPTION_CHANGE, updateSelectedServices);
    }

    private createFeatureTrackingsWithSingleOffer(variationId: string): FeatureTracking {
        const featureTracking = this.featureTrackingHelper.getFeatureTrackingFromHtml();
        const offerVariation = featureTracking.offerVariations.find(offer => offer.variationId === variationId);
        if (!offerVariation) {
            throw new Error(`could not find offer with id "${variationId}" in tracking data`);
        }
        return {
            ...featureTracking,
            offerVariations: [offerVariation],
        };
    }

    private trackSelectionChangeAction(featureTracking: FeatureTracking, service: FeatureTrackingOffersVariationService) {
        const modifiedFeatureTracking = cloneDeep(featureTracking);
        /*                                                                                                         */
        delete modifiedFeatureTracking.offerVariations[0].labels.product_DeliveryServiceType;
        modifiedFeatureTracking.offerVariations[0].offersVariationServices = [service];
        const actionName = service.status === 'selected' ? 'select' : 'deselect';
        this.trackAction(actionName, modifiedFeatureTracking);
    }

    private changeService(variationId: string, changedServiceId: string, isSelected: boolean): FeatureTrackingOffersVariationService {
        const featureTracking = this.featureTrackingHelper.getFeatureTrackingFromHtml();
        const offerVariation = featureTracking.offerVariations.find(offer => offer.variationId === variationId);
        const service = offerVariation?.offersVariationServices.find(service => service.labels.order_ServiceId === changedServiceId);

        if (!!service && !!offerVariation) {
            const serviceStatus = isSelected ? 'selected' : 'deselected';
            service.status = serviceStatus;
            service.labels.order_ServiceStatus = serviceStatus;
            this.featureTrackingHelper.setFeatureTrackingInHtml(featureTracking);
            return service;
        }
        throw new Error(`Service not found in Dom for serviceId: ${changedServiceId}`);
    }
}
